import { useContext, useEffect, useRef } from 'react'

import { AuthContext } from 'features/auth'
import { useProjectStore } from 'features/project/store'
import { identifyChameleon } from 'trackers/chameleon'
import { ProjectState } from 'types/project'

/**
 * Enriches the Chameleon user profile with hub and account context when the
 * active project changes, enabling hub/account-level targeting in Chameleon.
 * Yogi internal users are included so they can preview customer experiences;
 * you can exclude them in Chameleon segments via the @meetyogi.com email domain.
 */
export const useChameleonProject = (): void => {
  const authContext = useContext(AuthContext)
  const details = useProjectStore((state: ProjectState) => state.details)

  const lastTraitsKey = useRef<string | null>(null)

  useEffect(() => {
    const email = authContext.userDetails?.email?.toLowerCase()
    const hubUuid = details.proj_uuid
    if (!email || !hubUuid) return

    const traitsKey = `${hubUuid}|${details.account_uuid ?? ''}|${details.account_name ?? ''}`
    if (traitsKey === lastTraitsKey.current) return

    identifyChameleon(email, {
      hub_uuid: hubUuid,
      account_uuid: details.account_uuid,
      account_name: details.account_name,
    })
    lastTraitsKey.current = traitsKey
  }, [
    details.proj_uuid,
    details.account_uuid,
    details.account_name,
    authContext.userDetails,
  ])
}
